import React from "react";
import { Box } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { LoadingButton } from "./LoadingButton.js";
import { Portal } from "src/utils/Portal.js";
import { useModalScrollBlocker } from "src/utils/useModalScrollBlocker.js";
import { merge } from "lodash";

/**
 * Generic floating button that appears from the bottom with a backdrop
 * Features Partiful-style concentric outline rings and smooth animations
 *
 * @param {boolean} open - Controls button visibility
 * @param {boolean} loading - Shows loading state
 * @param {function} onClick - Click handler
 * @param {string} id - Optional custom ID for the button
 * @param {string} dataTestId - Optional test ID
 * @param {object} sx - Optional sx prop for customizing the button
 * @param {React.ReactNode} children - Button text/content
 */
const FloatingButton = ({
  open,
  loading,
  onClick,
  id = "floating-button",
  dataTestId = "floating-button",
  sx = {},
  children,
}) => {
  useModalScrollBlocker({ open, justKeyboardFixes: true });
  return (
    <Portal>
      <AnimatePresence>
        {open && (
          <>
            {/* Button Container with glass effect */}
            <Box
              component={motion.div}
              initial={{ y: 100, opacity: 0, scale: 0.9 }}
              animate={{
                y: [100, -12, 0],
                opacity: [0, 1, 1],
                scale: [0.9, 1.05, 1],
              }}
              exit={{
                y: [0, 8, 100],
                opacity: [1, 1, 0],
                scale: [1, 0.95, 0.9],
              }}
              transition={{
                duration: 0.5,
                times: [0, 0.6, 1],
                ease: "easeOut",
              }}
              sx={{
                position: "absolute",
                // `#portal-root` spans the viewport, so without the shell's
                // insets the button centres on the window instead of on the
                // content column the page is centred in. Zero outside the shell.
                left: "var(--discovery-content-left, 0px)",
                right: "var(--discovery-content-right, 0px)",
                // `--discovery-content-bottom` carries the mobile tab bar's
                // height so the button always rests above it.
                bottom: {
                  xs: "calc(-100dvh + 20px + var(--discovery-content-bottom, 0px))",
                  sm: "calc(-100dvh + 28px + var(--discovery-content-bottom, 0px))",
                },
                display: "flex",
                justifyContent: "center",
                zIndex: 501,
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  // Glass container background
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -8,
                    left: -8,
                    right: -8,
                    bottom: -8,
                    borderRadius: "999px",
                    background: "rgba(255, 255, 255, 0.08)",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",
                    border: "1px solid rgba(255, 255, 255, 0.18)",
                    pointerEvents: "none",
                    zIndex: -1,
                  },
                  // Outer glow ring
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: -12,
                    left: -12,
                    right: -12,
                    bottom: -12,
                    borderRadius: "999px",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    pointerEvents: "none",
                    zIndex: -2,
                  },
                }}
              >
                <LoadingButton
                  id={id}
                  data-testid={dataTestId}
                  variant="contained"
                  size="large"
                  loading={loading}
                  onClick={onClick}
                  sx={merge(
                    {
                      borderRadius: "999px",
                      px: 4,
                      py: 1.5,
                      fontSize: "1rem",
                      fontWeight: 600,
                      // Subtle shadow instead of heavy drop shadow to prevent iOS gap
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)",
                      // Glass effect on the button itself
                      backdropFilter: "blur(8px)",
                      WebkitBackdropFilter: "blur(8px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      position: "relative",
                      overflow: "hidden",
                      // Subtle inner highlight for glass effect
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        height: "50%",
                        background:
                          "linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%)",
                        pointerEvents: "none",
                        borderRadius: "999px 999px 0 0",
                      },
                      "&:hover": {
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.16)",
                        transform: "translateY(-1px)",
                        borderColor: "rgba(255, 255, 255, 0.3)",
                      },
                      "&:active": {
                        transform: "translateY(0)",
                      },
                      transition: "all 0.2s ease",
                    },
                    sx
                  )}
                >
                  {children}
                </LoadingButton>
              </Box>
            </Box>
          </>
        )}
      </AnimatePresence>
    </Portal>
  );
};

export default FloatingButton;
